import React, { useEffect, useState } from 'react';
import { useApiData } from '../../../contexts/apiService';
import './tableInput.css';
import TextInput from '../textInput/textInput';
import Dropdown from '../dropdown/dropdown';
import DateInput from '../date_input/dateInput';
import AddButton from '../button_add/addButton';
import LabelTooltip from '../label_tooltip/labelTooltip';
import { updateJsonArray } from '../../../helper_functions';
import TimeInput from '../time_input/timeInput';

export interface InputColumnConfig {
    colName: string;
    colType: 'text' | 'number' | 'boolean' | 'coordinate' | 'date' | 'time' | 'datetime';
    placeholder?: string;
    required?: boolean;
    submitValue?: boolean; // if this col should be added to row
    inputType?: 'input' | 'dropdown' | 'date' | 'time' | 'datetime' | 'typeahead';
    dropdownOptions?: Array<{ value: any; label: string; [key: string]: any }>;
    dependsOn?: string; // Column name this dropdown depends on
    getFilteredOptions?: (dependentValue: any, allOptions: Array<{ value: any; label: string; [key: string]: any }>) => Array<{ value: any; label: string; [key: string]: any }>;
    linkedGroup?: string[]; // Column names for group dependency
    getLinkedFilteredOptions?: (dependentValue: any, allOptions: any[]) => any[];
    infoTooltip?: string;
    calculateValue?: (values: Record<string, any>, modifiedField?: string, masterField?: string | null) => any; // Function to auto-calculate this field's value based on other values
    defaultValue?: any; // pre-fills the field when the form is (re)set, e.g. auto-filling the current user
}

export interface TableInputProps {
    tableName: string;
    parentId?: number; // for inserting rows in tables that have a parent-child relationship, to link the new row to its parent
    inputColumnConfigs: InputColumnConfig[];
    onValuesChange?: (values: Record<string, any>) => void;
    tableType?: 'json';
    jsonColName?: string;
    jsonColValue?: any;
    rowIDValue?: string | number;
    addMultipleRows?: boolean;  // enables adding more than one row at a time
    // style parameters
    arrangement?: 'row' | 'column';
    clearButton?: boolean; // whether to show a clear button
}

const TableInput: React.FC<TableInputProps> = ({ 
    tableName,
    parentId,
    inputColumnConfigs,
    onValuesChange,
    tableType,
    jsonColName,
    jsonColValue,
    rowIDValue,
    addMultipleRows = false,
    // style parameters
    arrangement,
    clearButton = true,
}) => {
    
    const { handleAddRow, handleEditOrder } = useApiData();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitError, setSubmitError] = useState<string | null>(null); // backend rejection of the last submit
    const [rowCount, setRowCount] = useState<number | null>(1);
    // For bidirectional field calculations (e.g., nominations_monthly <-> nominations_bpd):
    // masterField tracks which field the user last edited, so we know which direction to calculate
    const [masterField, setMasterField] = useState<string | null>(null);

    // Reset values when tableName or inputColumnConfigs change
    useEffect(() => {
        const emptyValues = resetValues();
        setValues(emptyValues);
        setErrors({}); // Clear errors too
        setSubmitError(null);
        
        // Notify parent component of the reset
        if (onValuesChange) {
            onValuesChange(emptyValues);
        }
    }, [tableName]);
    
    // Simple function to reset values to empty
    const resetValues = (preserveDateFields = false, currentValues?: Record<string, any>) => {
        const emptyValues: Record<string, any> = {};
        inputColumnConfigs.forEach(config => {
            // Preserve date fields after submitting
            if (preserveDateFields && config.colType === 'date' && currentValues) {
                emptyValues[config.colName] = currentValues[config.colName] || '';
            }

            // Set default value for status column in schedule_table
            else if (tableName === 'schedule_table' && config.colName === 'status') {
                emptyValues[config.colName] = 'Scheduled';
            } else if (
                (config.inputType === 'date' || config.inputType === 'datetime') &&
                config.placeholder
            ) {
                emptyValues[config.colName] = config.placeholder;
            }
            // Pre-fill any field that declares a defaultValue (e.g. the current user)
            else if (config.defaultValue !== undefined && config.defaultValue !== null && config.defaultValue !== '') {
                emptyValues[config.colName] = config.defaultValue;
            } else {
                emptyValues[config.colName] = '';
            }
        });
        return emptyValues;
    };
    const [values, setValues] = useState<Record<string, any>>(() => resetValues());

    function isValidTimeFormat(time: string): boolean {
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5]?[0-9]):([0-5]?[0-9])$/;
        const match = time.match(timeRegex);
        
        if (!match) return false;
        
        const hours = parseInt(match[1]);
        const minutes = parseInt(match[2]);
        const seconds = parseInt(match[3]);
        
        return hours >= 0 && hours <= 23 && 
            minutes >= 0 && minutes <= 59 && 
            seconds >= 0 && seconds <= 59;
    }

    function validateInputValues(
        inputColumnConfigs: InputColumnConfig[],
        values: Record<string, any>
    ): Record<string, string> {
        const errors: Record<string, string> = {};

        inputColumnConfigs.forEach(config => {
            const val = values[config.colName];

            // Number validation
            if (config.colType === 'number' && val !== '' && val !== undefined && val !== null) {
                if (isNaN(Number(val))) {
                    errors[config.colName] = 'Must be a valid number';
                    return;
                }
            }

            // Coordinate validation (latitude as example: number with dot, -180 to 180)
            if (config.colType === 'coordinate' && val !== '' && val !== undefined && val !== null) {
                if (!/^[-+]?\d{1,3}\.\d+$/.test(val)) {
                    errors[config.colName] = 'Must be a decimal number';
                    return;
                }
                const num = parseFloat(val);
                if (num < -180 || num > 180) {
                    errors[config.colName] = 'Must be between -180 and 180';
                    return;
                } else {
                    if (config.colName === 'latitude' && (num < 18 || num > 72)) {
                        errors[config.colName] = 'Latitude must be between 18° and 72° (USA)';
                        return;
                    }
                    
                    // USA longitude range: -180° to -66° (includes Alaska, Hawaii, continental US)
                    if (config.colName === 'longitude' && (num < -180 || num > -66)) {
                        errors[config.colName] = 'Longitude must be between -180° and -66° (USA)';
                        return;
                    }
                }
            }

            // DateTime validation
            if (config.inputType === 'datetime' && config.required) {
                if (typeof val === 'object' && (!val.date || !val.time)) {
                    errors[config.colName] = 'Both date and time are required';
                    return;
                } else if (typeof val === 'string' && !val.includes(' ')) {
                    errors[config.colName] = 'Complete datetime is required';
                    return;
                }
            }

            // Time format validation for datetime
            if (config.inputType === 'datetime' && val) {
                let timeToValidate = '';
                if (typeof val === 'string' && val.includes(' ')) {
                    const [, timePart] = val.split(' ');
                    timeToValidate = timePart || '';
                } else if (typeof val === 'object' && val.time) {
                    timeToValidate = val.time;
                }

                if (timeToValidate && !isValidTimeFormat(timeToValidate)) {
                    errors[config.colName] = 'Invalid time format (HH:MM:SS)';
                    return;
                }
            }
        });

        return errors;
    }

    // Check if all required fields are completed
    const areRequiredFieldsCompleted = () => {
        const requiredFields = inputColumnConfigs.filter(config => config.required);
        
        return requiredFields.every(config => {
            const value = values[config.colName];

            if (config.inputType === 'datetime') {
                if (typeof value === 'string') {
                    const [date, time] = value.split(' ');
                    return Boolean(date && time && isValidTimeFormat(time));
                }

                if (value && typeof value === 'object') {
                    return Boolean(
                        value.date &&
                        value.time &&
                        isValidTimeFormat(value.time)
                    );
                }

                return false;
            }

            return value !== undefined && value !== null && value !== '';
        });
    };

    const isButtonDisabled = !areRequiredFieldsCompleted() || isSubmitting || rowCount === 0 || rowCount === null;

    // Handle input changes
    const handleInputChange = (config: InputColumnConfig, inputValue: any) => {
        let processedValue = inputValue;

        if (config.colType === 'boolean') {
            // For boolean fields: only allow 0 or 1
            if (inputValue === '') {
                processedValue = '';
            } else if (inputValue === '0' || inputValue === '1') {
                processedValue = Number(inputValue);
            } else {
                // Don't update if input is not 0 or 1
                return;
            }
        }
        
        // Update values
        const newValues = {
            ...values,
            [config.colName]: processedValue
        };

        // If a numeric field with calculateValue is being modified directly by the user, it becomes the master
        const currentConfig = inputColumnConfigs.find(c => c.colName === config.colName);
        const hasCalculateValue = currentConfig?.calculateValue;
        const isNumericField = currentConfig?.colType === 'number';

        if (hasCalculateValue && isNumericField) {
            setMasterField(config.colName);
        }

        // Clear dependent fields when parent field changes
        inputColumnConfigs.forEach(otherConfig => {
            if (otherConfig.dependsOn === config.colName && newValues[otherConfig.colName]) {
                newValues[otherConfig.colName] = '';
            }
            // Also clear linkedGroup dependent fields
            if (otherConfig.linkedGroup?.includes(config.colName) && newValues[otherConfig.colName]) {
                newValues[otherConfig.colName] = '';
            }
        });

        // 2. Auto-set with cascading until nothing new resolves
        let madeChange = true;
        while (madeChange) {
            madeChange = false;
            inputColumnConfigs.forEach(otherConfig => {
                if (newValues[otherConfig.colName]) return;
                if (!otherConfig.linkedGroup?.length || !otherConfig.getLinkedFilteredOptions) return;
                if (!otherConfig.linkedGroup.some(dep => newValues[dep])) return; // no group member has a value

                const groupValues: Record<string, any> = {};
                otherConfig.linkedGroup.forEach(dep => { groupValues[dep] = newValues[dep]; });

                const filtered = otherConfig.getLinkedFilteredOptions(groupValues, otherConfig.dropdownOptions || []);
                if (filtered.length === 1) {
                    newValues[otherConfig.colName] = filtered[0].value;
                    madeChange = true;
                }
            });
        }

        // Auto-calculate values for fields that have calculateValue function
        inputColumnConfigs.forEach(otherConfig => {
            if (otherConfig.calculateValue && otherConfig.colName !== config.colName) {
                // Pass the modified field info and master field to calculateValue
                const calculatedValue = otherConfig.calculateValue(newValues, config.colName, masterField);
                if (calculatedValue !== undefined && calculatedValue !== null && calculatedValue !== '') {
                    newValues[otherConfig.colName] = calculatedValue;
                }
            }
        });

        setValues(newValues);

        // Call the callback function if provided
        if (onValuesChange) {
            onValuesChange(newValues);
        }
    };
    
    const handleAddNewRow = async () => {
        try {
            setIsSubmitting(true);
            
            // Validate fields
            const newErrors = validateInputValues(inputColumnConfigs, values);

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                setIsSubmitting(false);
                return;
            } else {
                setErrors({});
            }
            
            // Convert number fields to numbers, if empty set to null
            const processedValues: Record<string, any> = {};

            inputColumnConfigs.forEach(config => {
                // Skip fields where submitValue is explicitly set to false
                if (config.submitValue === false) {
                    return;
                }
                
                const value = values[config.colName];
            
                if (config.colType === 'number' || config.colType === 'coordinate') {
                    if (value !== '') {
                        processedValues[config.colName] = Number(value);
                    } else {
                        processedValues[config.colName] = null;
                    }
                } else {
                    processedValues[config.colName] = value;
                }
            });
            
            if (tableName === 'schedule_table' && !processedValues['status']) {
                processedValues['status'] = 'Scheduled';
            }

            if (tableName === 'crude_tanks_definition' && parentId) {
                processedValues['lease_id'] = parentId;
            }

            if (tableName === 'entity_producer_history' && parentId) {
                processedValues['entity_id'] = parentId;
            }

            if (tableName === 'carrier_pricing_producer' && parentId) {
                processedValues['pricing_id'] = parentId;
            }

            if (tableType == 'json' && rowIDValue && jsonColName) {
                const updatedJsonString = updateJsonArray(jsonColValue, 'add', processedValues);
                await handleEditOrder(tableName, rowIDValue, jsonColName, jsonColValue, updatedJsonString);
            
            } else {
                if (rowCount === null || rowCount < 1) {
                    setIsSubmitting(false);
                    return;
                }
                
                const added = await handleAddRow(tableName, processedValues, rowCount);
                // Automatic Scheduling: if the backend rejects the rule (e.g. End Date before
                // Start Date), keep what the user typed and say so instead of clearing the form.
                if (!added && tableName === 'automate_scheduling') {
                    // Dates are 'YYYY-MM-DD', so string comparison orders them by date
                    const datesInverted = processedValues.startDate && processedValues.endDate
                        && processedValues.endDate < processedValues.startDate;
                    setSubmitError(datesInverted
                        ? 'End Date must be on or after Start Date.'
                        : 'Could not add the rule. Please try again.');
                    return;
                }
            }
            setSubmitError(null);
            
            // reset fields after submission
            const emptyValues = resetValues(true, values);
            setValues(emptyValues);
            setRowCount(1);

            // notify parent component of the reset
            if (onValuesChange) {
                onValuesChange(emptyValues);
            }
            
        } catch (error) {
            console.error('Error adding row:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDateTimeChange = (config: InputColumnConfig, inputValue: any, inputSubType: 'date' | 'time') => {
        const currentValue = values[config.colName] || '';
        let newDateTime = '';
        
        if (inputSubType === 'date') {
            // Extract existing time part if datetime exists
            const existingTime = typeof currentValue === 'string' && currentValue.includes(' ') 
                ? currentValue.split(' ')[1] 
                : '';
            
            // Combine new date with existing time, or just set date if no time
            newDateTime = inputValue ? (existingTime ? `${inputValue} ${existingTime}` : inputValue) : '';
        } else if (inputSubType === 'time') {
            // Extract existing date part if datetime exists
            const existingDate = typeof currentValue === 'string' && currentValue.includes(' ') 
                ? currentValue.split(' ')[0] 
                : currentValue || '';
            
            // Combine existing date with new time, format time with :00 if needed
            if (existingDate && inputValue) {
                const formattedTime = inputValue.includes(':') && inputValue.split(':').length === 2 
                    ? `${inputValue}:00` 
                    : inputValue;
                newDateTime = `${existingDate} ${formattedTime}`;
            } else if (inputValue) {
                newDateTime = inputValue;
            } else {
                newDateTime = existingDate;
            }
        }
        
        // Send the combined datetime string to handleInputChange
        handleInputChange(config, newDateTime);
    };

    // Function to render the appropriate input component
    const renderInputComponent = (config: InputColumnConfig) => {
        const value = values[config.colName];
        const basePlaceholder = config.placeholder || `Enter ${config.colName}...`;
        const placeholder = config.required ? `${basePlaceholder} *` : basePlaceholder;

        const renderInput = () => {
            // If inputType is 'dropdown', render Dropdown
            if (config.inputType === 'dropdown') {
                let dropdownOptions = config.dropdownOptions || [];
                let isDropdownDisabled = isSubmitting;
                
                // Handle dependent dropdowns (one-way)
                if (config.dependsOn && config.getFilteredOptions) {
                    const dependentValue = values[config.dependsOn];
                    if (!dependentValue) {
                        // Disable dropdown if dependent value is not selected
                        isDropdownDisabled = true;
                        dropdownOptions = [];
                    } else {
                        // Filter options based on dependent value
                        dropdownOptions = config.getFilteredOptions(dependentValue, config.dropdownOptions || []);
                    }
                }
                
                // Handle bidirectional dependencies (two-way)
                if (config.linkedGroup?.length && config.getLinkedFilteredOptions) {
                    const hasAnyGroupValue = config.linkedGroup.some(dep => values[dep]);
                    if (hasAnyGroupValue) {
                        // Filter options based on linked group values
                        const groupValues: Record<string, any> = {};
                        config.linkedGroup.forEach(dep => { groupValues[dep] = values[dep]; });
                        dropdownOptions = config.getLinkedFilteredOptions(groupValues, config.dropdownOptions || []);
                    }
                    // Don't disable - allow selection even if bidirectional field is empty
                }
                
                return (
                    <Dropdown
                        value={value}
                        onChange={(selectedValue) => handleInputChange(config, selectedValue)}
                        options={dropdownOptions}
                        placeholder={placeholder}
                        isDisabled={isDropdownDisabled}
                        className="dropdown-inputMatch"
                    />
                );
            }

            // render date component
            if (config.inputType === 'date') {
                return (
                    <DateInput
                        value={value}
                        onChange={(inputValue) => handleInputChange(config, inputValue)}
                        disabled={isSubmitting}
                        placeholder={basePlaceholder}
                    />
                );
            }

            // render date component
            if (config.inputType === 'datetime') {
                // Extract date and time parts from the current value
                const currentValue = values[config.colName] || '';
                const dateValue = typeof currentValue === 'string' && currentValue.includes(' ') 
                    ? currentValue.split(' ')[0] 
                    : currentValue;
                const timeValue = typeof currentValue === 'string' && currentValue.includes(' ') 
                    ? currentValue.split(' ')[1]?.replace(':00', '') || ''
                    : '';

                return (
                    <div style={{ gap: '8px', display: 'flex' }}>
                        <DateInput
                            value={dateValue}
                            onChange={(inputValue) => handleDateTimeChange(config, inputValue, 'date')}
                            disabled={isSubmitting}
                            placeholder={basePlaceholder}
                        />
                        <TimeInput
                            value={timeValue}
                            onChange={(inputValue) => handleDateTimeChange(config, inputValue, 'time')}
                            disabled={isSubmitting}
                            placeholder="00:00:00"
                        />
                    </div>
                );
            }

            // Default to TextInput for 'input' type or when inputType is not specified
            return (
                <div style={{ position: 'relative', width: '100%' }}>
                    <TextInput
                        id={`${tableName}-${config.colName}`}
                        value={value}
                        onChange={(inputValue) => handleInputChange(config, inputValue)}
                        placeholder={placeholder}
                        disabled={isSubmitting}
                    />
                    {errors[config.colName] && (
                        <div className='tableInput-errorMessage'>
                            {errors[config.colName]}
                        </div>
                    )}
                </div>
            );
        };

        // Wrap input with tooltip if infoTooltip is provided
        if (config.infoTooltip) {
            return (
                <LabelTooltip
                    tooltip={config.infoTooltip}
                    position="top"
                >
                    {renderInput()}
                </LabelTooltip>
            );
        }

        return renderInput();
    };

    const isFormEmpty = () => {
        const defaults = resetValues();

        return inputColumnConfigs.every(config => {
            const value = values[config.colName];

            return value === defaults[config.colName]
                || value === ''
                || value === null
                || value === undefined;
        })
    };

    const handleClearForm = () => {
        const emptyValues = resetValues();
        setValues(emptyValues);
        setErrors({});
        setSubmitError(null);
        setMasterField(null);
        
        // Notify parent component of the reset
        if (onValuesChange) {
            onValuesChange(emptyValues);
        }
    };

    const isClearButtonDisabled = isSubmitting || isFormEmpty();

    return (
        <>
        <div 
            className="tableInput-mainContainer"
            style={{flexDirection: arrangement ? arrangement : 'row'}}
        >   

            {clearButton && (
                <AddButton
                    onClick={handleClearForm}
                    disabled={isClearButtonDisabled}
                    text="Clear"
                />
            )}

            <AddButton
                onClick={handleAddNewRow}
                disabled={isButtonDisabled}
            />
            
            {inputColumnConfigs.map((config) => {
                return (
                    <div key={config.colName} data-col={config.colName}>
                        {renderInputComponent(config)}
                    </div>
                );
            })}

            {addMultipleRows && (
                <LabelTooltip
                    tooltip={'Number of rows to add (max 25)'}
                    position="top"
                >
                    <TextInput
                        value={rowCount?.toString()}
                        onChange={(value) => {
                            const numValue = parseInt(value);
                            if (value === '') {
                                setRowCount(null);
                            }
                            if (!isNaN(numValue) && numValue >= 0 && numValue <= 25) { // Max 25 rows
                                setRowCount(numValue);
                            } 
                        }}
                        disabled={isSubmitting}
                        width={40}
                    />
                </LabelTooltip>
            )}
        </div>
        {submitError && (
            <div className='tableInput-submitError' role='alert'>
                {submitError}
            </div>
        )}
        </>
    );
};

export default TableInput;
